import { FC } from "react";
import LmsTable from "../ui/LmsTable";
import LmsTabPages from "../ui/LmsTabPages";
import {
  LmsLoanDetailsTable,
  LmsLoanPropertyTable,
  LmsLoanConditionsTable,
  LmsLoanCommentsTable,
  LmsLoanDetailsEntitiesByLoanIdTable,
} from "../ui/LmsSharedTables";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { FeatureFlags } from "../../constants/LocalServerConstants";

interface LmsVotingBTBP2ReviewEditFormProps {
  id: string;
}

const LmsVotingBTBP2ReviewMainPage: FC = () => {
  return (
    <LmsTable
      skipClick
      actionButtons={["edit", "export"]}
      displayProps={["borrower_Organization"]}
      apiSuffix="voting/loan-phase2-pollings"
      columnLabelsDictionary={{
        loanNumber: "Loan #",
        applicationNumber: "Application #",
        perVoted: "Voting %",
        borrower_Organization: "Borrower",
      }}
    />
  );
};

const LmsVotingBTBP2ReviewEditForm: FC<LmsVotingBTBP2ReviewEditFormProps> = ({
  id,
}) => {
  const [{ data: btbPrReviewData }] = useFetchData(`voting/btb-p2-review/${id}`);
  const [{ data: ircDecisiondata, isLoading, error }] = useFetchData(`voting/btb-p2-lrc-decision-info/${id}`);
  const updatedData = { ...btbPrReviewData, ...ircDecisiondata };
  const loanId = btbPrReviewData?.loanDetailId || "";
  if (FeatureFlags.IS_DEBUG) {
    console.log(id);
  }

  const myForm = new BCFormController(updatedData);
  myForm.addCenteredGroup([
    myForm.getDisplayField("Borrower", { name: "borrowerOrganizationCSV" }),
    myForm.getDisplayField("Loan #", { name: "loanNumberDisplay" }),
  ]);

  myForm.addCenteredGroup([
    myForm.getLabelField("Property"),
    myForm.getListField("Property", { listOptions: updatedData?.loanProperty_Streets, listItemType: "Text" }),
    myForm.getDisplayField("Project Name", { name: "name" }),
  ]);
  myForm.addCenteredGroup(
    [
      myForm.getDisplayField("City", { width: 2, value: updatedData?.city?.display }),
      myForm.getDisplayField("State", { width: 2, value: updatedData?.state?.display }),
      myForm.getDisplayField("Submarket", { width: 2, name: "loanProject_Submarket" }),
      myForm.getInputField("Budget", { width: 2, name: "loanBudgetAmount", fieldType: "dollar", disabled: true }),
    ],
    { widths: 2 }
  );

  myForm.addCenteredGroup([
    myForm.getTextareaField("LRC Overview", { disabled: true, name: "loanUnderWriting_LrcOverview" }),
  ]);

  myForm.addCenteredGroup(
    [
      myForm.getDisplayField("Original Projected IRR", { width: 2, value: updatedData?.loanUnderwriting?.projectedIrroriginal }),
      myForm.getDisplayField("Current LTV", { name: "loanUnderWriting_LtvPercentage", width: 2 }),
      myForm.getDisplayField("Current CLTV", { width: 2, name: "loanUnderWriting_CltVPercentage" }),
      myForm.getDisplayField("Current LTC", { width: 2, name: "loanUnderWriting_LtcPercentage" }),
    ],
    { widths: 2 }
  );

  myForm.addCenteredGroup(
    [
      myForm.getDisplayField("BTBP2 Revised IRR", { width: 2, name: "btbp2revisedIrr" }),
      myForm.getDisplayField("Recording Date", { width: 2, name: "loanClosing_ActualClosingDate" }),
      myForm.getCheckboxField("Direct Pay", { width: 2, disabled: true, name: "loanDetail_DirectPay" }),
      myForm.getDisplayField("Fund", { width: 2 }),
    ],
    { widths: 2 }
  );

  myForm.addCenteredGroup([
    myForm.getTextareaField("BTBP2 LRC Revision", { name: "btbp2lrcrevision", disabled: true }),
    myForm.getTextareaField("BTBP2 Narrative", { name: "btbp2narrative", disabled: true }),
  ]);
  
  myForm.addCenteredGroup([myForm.addDisplayField("Name of Loan Officer", { name: "loanOfficer" })]);
  myForm.addCenteredGroup([
    myForm.getButtonField("View Loan Details", `/loandetails/${loanId}`),
  ]);

  myForm.addComponent(
    <>
    <LmsTable apiSuffix={`voting/loan-review/list-loan-docs/${id}`} title="Documents" skipFilter />
    <LmsTable apiSuffix={`voting/btb-p2-lrc-results/${id}`} title="Members Vote" skipFilter skipClick />
    </>
  );

  const editFormTabPages = {
    Details: (
      <LmsLoanDetailsTable
        overrideDefaultTableProps={{
          apiSuffix: `voting/loan-review/list-loan-details/${id}`,
          actionButtons: [
            { action: "edit", path: "loandetails" },
            "export",
          ],
        }}
      />
    ),
    Properties: <LmsLoanPropertyTable loanId={loanId} />,
    Entities: <LmsLoanDetailsEntitiesByLoanIdTable loanId={loanId} />,
    Comments: <LmsLoanCommentsTable loanId={id} />,
    Conditions: (
      <LmsLoanConditionsTable
        overrideDefaultTableProps={{
          apiSuffix: `loans/conditions/${id}`,
          actionButtons: ["export"],
        }}
      />
    ),
  };
  return (
    <>
      <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={["close"]} />
      <LmsTabPages tabPages={editFormTabPages} />
    </>
  );
};

const LmsVotingBTBP2Review = ({ params = {}, history }) => {
  const { isMainPage, isEdit, isActionAllowed, id } = useParseRouteActions(
    params,
    ["edit"]
  );
  if (!isActionAllowed) {
    history.goBack();
  }
  return (
    <>
      {isMainPage && <LmsVotingBTBP2ReviewMainPage />}
      {isEdit && <LmsVotingBTBP2ReviewEditForm id={id} />}
    </>
  );
};

export default LmsVotingBTBP2Review;
